import React, { useState, useEffect } from 'react';
import eventAPI from '../services/eventAPI';
import RetroInput from './ui/RetroInput';
import RetroTextarea from './ui/RetroTextarea';
import RetroDateTimePicker from './ui/RetroDateTimePicker';
import './EventDetailsModal.css';

function EventDetailsModal({ eventId, onClose, onEventUpdated, currentUserId }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  // Voting state
  const [selectedVotes, setSelectedVotes] = useState({});  // { optionId: rank }

  // Counter-proposal state
  const [showCounterProposal, setShowCounterProposal] = useState(false);
  const [counterProposalType, setCounterProposalType] = useState('datetime');
  const [counterProposalValue, setCounterProposalValue] = useState('');

  // Finalization state
  const [selectedDatetimeOption, setSelectedDatetimeOption] = useState(null);
  const [selectedLocationOption, setSelectedLocationOption] = useState(null);

  useEffect(() => {
    loadEventDetails();
    loadComments();
  }, [eventId]);

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      const data = await eventAPI.getEvent(eventId);
      setEvent(data);

      // Load user's existing votes
      if (data.user_votes) {
        const votes = {};
        data.user_votes.forEach(vote => {
          votes[vote.option_id] = vote.rank;
        });
        setSelectedVotes(votes);
      }
    } catch (error) {
      console.error('Failed to load event:', error);
      setError('Event konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const data = await eventAPI.getComments(eventId);
      setComments(data.comments || []);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleVoteChange = (optionId, rank, optionType) => {
    setSelectedVotes(prev => {
      // If rank is null, remove the vote
      if (rank === null) {
        const { [optionId]: _, ...rest } = prev;
        return rest;
      }

      // Get all options of the same type
      const sameTypeOptions = event.options?.filter(opt => opt.option_type === optionType) || [];
      const sameTypeOptionIds = sameTypeOptions.map(opt => opt.id);

      // Check if another option OF THE SAME TYPE already has this rank
      const optionWithSameRank = Object.entries(prev).find(
        ([id, r]) => r === rank && id !== optionId && sameTypeOptionIds.includes(id)
      );

      if (optionWithSameRank) {
        // Swap ranks within the same category
        const newVotes = { ...prev };
        newVotes[optionWithSameRank[0]] = prev[optionId] || null;
        newVotes[optionId] = rank;
        return newVotes;
      }

      return { ...prev, [optionId]: rank };
    });
  };

  const handleSubmitVotes = async () => {
    try {
      const votes = Object.entries(selectedVotes)
        .filter(([_, rank]) => rank !== null)
        .map(([option_id, rank]) => ({ option_id, rank }));

      await eventAPI.submitVotes(eventId, votes);
      alert('Abstimmung gespeichert!');
      loadEventDetails();
    } catch (error) {
      console.error('Failed to submit votes:', error);
      alert('Abstimmung konnte nicht gespeichert werden');
    }
  };

  const handleRespondToInvitation = async (status) => {
    try {
      await eventAPI.respondToInvitation(eventId, status);
      loadEventDetails();
      if (onEventUpdated) onEventUpdated();
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      alert('Antwort konnte nicht gespeichert werden');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await eventAPI.addComment(eventId, newComment);
      setNewComment('');
      loadComments();
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Kommentar konnte nicht hinzugefügt werden');
    }
  };

  const handleSubmitCounterProposal = async (e) => {
    e.preventDefault();
    if (!counterProposalValue.trim()) return;

    try {
      await eventAPI.addCounterProposal(eventId, counterProposalType, counterProposalValue);
      setShowCounterProposal(false);
      setCounterProposalValue('');
      loadEventDetails();
      alert('Gegenvorschlag eingereicht!');
    } catch (error) {
      console.error('Failed to add counter-proposal:', error);
      alert('Gegenvorschlag konnte nicht eingereicht werden');
    }
  };

  const handleApproveCounterProposal = async (optionId, approved) => {
    try {
      await eventAPI.approveCounterProposal(eventId, optionId, approved);
      loadEventDetails();
    } catch (error) {
      console.error('Failed to approve counter-proposal:', error);
      alert('Aktion konnte nicht ausgeführt werden');
    }
  };

  const handleFinalizeEvent = async () => {
    if (!selectedDatetimeOption) {
      alert('Bitte wähle eine Datum/Zeit Option aus');
      return;
    }

    try {
      await eventAPI.finalizeEvent(eventId, selectedDatetimeOption, selectedLocationOption);
      loadEventDetails();
      if (onEventUpdated) onEventUpdated();
      alert('Event finalisiert!');
    } catch (error) {
      console.error('Failed to finalize event:', error);
      alert('Event konnte nicht finalisiert werden');
    }
  };

  const handleDeleteEvent = async () => {
    const confirmed = window.confirm('Möchtest du dieses Event wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.');

    if (!confirmed) return;

    try {
      await eventAPI.deleteEvent(eventId);
      if (onEventUpdated) onEventUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Event konnte nicht gelöscht werden');
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Lädt...</div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="error">{error || 'Event nicht gefunden'}</div>
          <button onClick={onClose}>Schließen</button>
        </div>
      </div>
    );
  }

  const isCreator = event.is_creator;
  const isPlanningEvent = event.event_type === 'planning';
  const isScheduled = event.status === 'scheduled';
  const myInvitation = event.invitations?.find(inv => inv.user_id === currentUserId);

  const datetimeOptions = event.options?.filter(opt => opt.option_type === 'datetime' && opt.approved) || [];
  const locationOptions = event.options?.filter(opt => opt.option_type === 'location' && opt.approved) || [];
  const counterProposals = event.options?.filter(opt => opt.is_counter_proposal && !opt.approved) || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content event-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{event.title}</h2>
          <div className="modal-header-actions">
            {isCreator && (
              <button
                className="modal-delete"
                onClick={handleDeleteEvent}
                title="Event löschen"
              >
                [X]
              </button>
            )}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="event-details-content">
          {/* Event Info */}
          <section className="details-section">
            <div className="event-meta">
              <span className={`event-type-badge event-type-${event.event_type}`}>
                {event.event_type === 'planning' ? '[V] PLANUNG' : '[#] FESTGELEGT'}
              </span>
              <span className={`event-status-badge event-status-${event.status}`}>
                {event.status.toUpperCase()}
              </span>
              <span className={`event-color-badge color-${event.color}`}>
                [{event.color?.toUpperCase()}]
              </span>
              <span className="event-creator">von {event.creator_username}</span>
            </div>

            {event.description && (
              <p className="event-description">{event.description}</p>
            )}

            {isScheduled && event.finalized_datetime && (
              <div className="event-datetime">
                <span className="datetime-icon">[#]</span>
                <span>{new Date(event.finalized_datetime).toLocaleString('de-DE')}</span>
              </div>
            )}

            {event.finalized_location && (
              <div className="event-location">
                <span className="location-icon">[@]</span>
                <span>{event.finalized_location}</span>
              </div>
            )}

            {isPlanningEvent && event.voting_deadline && !event.voting_closed_at && (
              <div className="voting-deadline">
                <span className="deadline-icon">[!]</span>
                Abstimmung endet: {new Date(event.voting_deadline).toLocaleString('de-DE')}
              </div>
            )}
          </section>

          {/* Voting Section (for planning events) */}
          {isPlanningEvent && !event.voting_closed_at && (
            <section className="details-section voting-section">
              <h3 className="section-title">
                <span className="section-icon">[V]</span>
                ABSTIMMUNG
              </h3>

              {/* Datetime Options */}
              {datetimeOptions.length > 0 && (
                <div className="voting-group">
                  <h4 className="voting-group-title">Datum & Zeit</h4>
                  <div className="vote-options">
                    {datetimeOptions.map(option => (
                      <VoteOption
                        key={option.id}
                        option={option}
                        selectedRank={selectedVotes[option.id]}
                        onRankChange={(rank) => handleVoteChange(option.id, rank, 'datetime')}
                        allOptions={datetimeOptions}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Location Options */}
              {locationOptions.length > 0 && (
                <div className="voting-group">
                  <h4 className="voting-group-title">Ort</h4>
                  <div className="vote-options">
                    {locationOptions.map(option => (
                      <VoteOption
                        key={option.id}
                        option={option}
                        selectedRank={selectedVotes[option.id]}
                        onRankChange={(rank) => handleVoteChange(option.id, rank, 'location')}
                        allOptions={locationOptions}
                      />
                    ))}
                  </div>
                </div>
              )}

              <button className="submit-votes-btn" onClick={handleSubmitVotes}>
                ABSTIMMUNG SPEICHERN
              </button>

              {/* Counter-Proposal Button */}
              {!showCounterProposal && (
                <button
                  className="counter-proposal-btn"
                  onClick={() => setShowCounterProposal(true)}
                >
                  + GEGENVORSCHLAG MACHEN
                </button>
              )}

              {/* Counter-Proposal Form */}
              {showCounterProposal && (
                <form className="counter-proposal-form" onSubmit={handleSubmitCounterProposal}>
                  <h4 className="form-subtitle">Gegenvorschlag</h4>

                  <div className="form-row">
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="datetime"
                        checked={counterProposalType === 'datetime'}
                        onChange={(e) => setCounterProposalType(e.target.value)}
                      />
                      <span>Datum/Zeit</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="location"
                        checked={counterProposalType === 'location'}
                        onChange={(e) => setCounterProposalType(e.target.value)}
                      />
                      <span>Ort</span>
                    </label>
                  </div>

                  {counterProposalType === 'datetime' ? (
                    <RetroDateTimePicker
                      value={counterProposalValue}
                      onChange={(e) => setCounterProposalValue(e.target.value)}
                      required
                    />
                  ) : (
                    <RetroInput
                      value={counterProposalValue}
                      onChange={(e) => setCounterProposalValue(e.target.value)}
                      placeholder="Ort eingeben..."
                      required
                    />
                  )}

                  <div className="form-actions">
                    <button type="button" onClick={() => setShowCounterProposal(false)}>
                      Abbrechen
                    </button>
                    <button type="submit">Einreichen</button>
                  </div>
                </form>
              )}

              {/* Pending Counter-Proposals (for creator) */}
              {isCreator && counterProposals.length > 0 && (
                <div className="counter-proposals-list">
                  <h4 className="section-subtitle">Ausstehende Gegenvorschläge</h4>
                  {counterProposals.map(option => (
                    <div key={option.id} className="counter-proposal-item">
                      <div className="counter-proposal-info">
                        <span className="counter-proposal-type">
                          {option.option_type === 'datetime' ? '[#]' : '[@]'}
                        </span>
                        <span className="counter-proposal-value">
                          {option.option_type === 'datetime'
                            ? new Date(option.option_value).toLocaleString('de-DE')
                            : option.option_value
                          }
                        </span>
                        <span className="counter-proposal-by">
                          von {option.proposed_by_username}
                        </span>
                      </div>
                      <div className="counter-proposal-actions">
                        <button
                          className="approve-btn"
                          onClick={() => handleApproveCounterProposal(option.id, true)}
                        >
                          ✓
                        </button>
                        <button
                          className="reject-btn"
                          onClick={() => handleApproveCounterProposal(option.id, false)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Finalization Section (for creator of planning events) */}
          {isCreator && isPlanningEvent && !event.voting_closed_at && (
            <section className="details-section finalize-section">
              <h3 className="section-title">
                <span className="section-icon">✓</span>
                EVENT FINALISIEREN
              </h3>

              <div className="finalize-options">
                <div className="finalize-group">
                  <label className="finalize-label">Datum/Zeit wählen *</label>
                  {datetimeOptions.map(option => (
                    <label key={option.id} className="finalize-option">
                      <input
                        type="radio"
                        name="datetime"
                        value={option.id}
                        checked={selectedDatetimeOption === option.id}
                        onChange={() => setSelectedDatetimeOption(option.id)}
                      />
                      <span>
                        {new Date(option.option_value).toLocaleString('de-DE')}
                        {option.votes && <span className="vote-count">({option.votes.total} Stimmen)</span>}
                      </span>
                    </label>
                  ))}
                </div>

                {locationOptions.length > 0 && (
                  <div className="finalize-group">
                    <label className="finalize-label">Ort wählen</label>
                    {locationOptions.map(option => (
                      <label key={option.id} className="finalize-option">
                        <input
                          type="radio"
                          name="location"
                          value={option.id}
                          checked={selectedLocationOption === option.id}
                          onChange={() => setSelectedLocationOption(option.id)}
                        />
                        <span>
                          {option.option_value}
                          {option.votes && <span className="vote-count">({option.votes.total} Stimmen)</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <button className="finalize-btn" onClick={handleFinalizeEvent}>
                EVENT JETZT FINALISIEREN
              </button>
            </section>
          )}

          {/* Invitation Response (for invited users) */}
          {myInvitation && myInvitation.status === 'pending' && isScheduled && (
            <section className="details-section invitation-section">
              <h3 className="section-title">
                <span className="section-icon">[M]</span>
                EINLADUNG
              </h3>
              <p>Möchtest du an diesem Event teilnehmen?</p>
              <div className="invitation-actions">
                <button
                  className="invitation-btn accept-btn"
                  onClick={() => handleRespondToInvitation('accepted')}
                >
                  ✓ ZUSAGEN
                </button>
                <button
                  className="invitation-btn maybe-btn"
                  onClick={() => handleRespondToInvitation('maybe')}
                >
                  ? VIELLEICHT
                </button>
                <button
                  className="invitation-btn decline-btn"
                  onClick={() => handleRespondToInvitation('declined')}
                >
                  ✕ ABSAGEN
                </button>
              </div>
            </section>
          )}

          {/* Invitations List */}
          {event.invitations && event.invitations.length > 0 && (
            <section className="details-section invitations-list-section">
              <h3 className="section-title">
                <span className="section-icon">👥</span>
                EINGELADENE ({event.invitations.length})
              </h3>
              <div className="invitations-list">
                {event.invitations.map(inv => (
                  <div key={inv.id} className="invitation-item">
                    <span className="invitation-user">{inv.username}</span>
                    <span className={`invitation-status status-${inv.status}`}>
                      {inv.status === 'accepted' && '✓ Zugesagt'}
                      {inv.status === 'declined' && '✕ Abgesagt'}
                      {inv.status === 'maybe' && '? Vielleicht'}
                      {inv.status === 'pending' && '⏳ Ausstehend'}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Comments Section */}
          <section className="details-section comments-section">
            <h3 className="section-title">
              <span className="section-icon">💬</span>
              KOMMENTARE ({comments.length})
            </h3>

            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments">Noch keine Kommentare</p>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <span className="comment-author">{comment.username}</span>
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleString('de-DE')}
                      </span>
                    </div>
                    <p className="comment-text">{comment.comment_text}</p>
                  </div>
                ))
              )}
            </div>

            <form className="comment-form" onSubmit={handleAddComment}>
              <RetroTextarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Kommentar schreiben..."
                rows={3}
              />
              <button type="submit" className="comment-submit-btn">
                KOMMENTAR HINZUFÜGEN
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

// Vote Option Component
function VoteOption({ option, selectedRank, onRankChange, allOptions }) {
  const displayValue = option.option_type === 'datetime'
    ? new Date(option.option_value).toLocaleString('de-DE')
    : option.option_value;

  const totalVotes = option.votes?.total || 0;
  const votesByRank = option.votes?.by_rank || {};
  const voters = option.votes?.voters || [];

  // Determine number of ranks based on option count
  const optionCount = (allOptions || [option]).length;
  const maxRank = Math.min(optionCount, 3); // Cap at 3 for now
  const availableRanks = Array.from({ length: maxRank }, (_, i) => i + 1);

  // Calculate weighted score dynamically
  // rank 1 = maxRank points, rank 2 = maxRank-1 points, etc.
  const score = availableRanks.reduce((sum, rank) => {
    const points = maxRank - rank + 1;
    return sum + (votesByRank[rank] || 0) * points;
  }, 0);

  // Calculate max score across all options to determine leader
  const maxScore = Math.max(...(allOptions || [option]).map(opt => {
    const byRank = opt.votes?.by_rank || {};
    return availableRanks.reduce((sum, rank) => {
      const points = maxRank - rank + 1;
      return sum + (byRank[rank] || 0) * points;
    }, 0);
  }));

  const isLeading = score > 0 && score === maxScore;

  // Group voters by rank dynamically
  const votersByRank = {};
  availableRanks.forEach(rank => {
    votersByRank[rank] = voters.filter(v => v.rank === rank);
  });

  return (
    <div className={`vote-option ${isLeading ? 'leading' : ''}`}>
      <div className="vote-option-header">
        <span className="vote-option-value">
          {isLeading && <span className="leading-icon">[★] </span>}
          {displayValue}
        </span>
        {option.is_counter_proposal && (
          <span className="counter-proposal-badge">Gegenvorschlag</span>
        )}
      </div>

      <div className="vote-option-stats">
        <div className="vote-score-display">
          <span className="vote-score-label">Punkte:</span>
          <span className="vote-score-value">{score}</span>
          {isLeading && score > 0 && <span className="leading-badge">FÜHRT</span>}
        </div>
        {totalVotes > 0 && (
          <span className="vote-total-count">{totalVotes} {totalVotes === 1 ? 'Stimme' : 'Stimmen'}</span>
        )}
      </div>

      {/* Show who voted for what */}
      {totalVotes > 0 && (
        <div className="vote-voters-list">
          {availableRanks.map(rank => {
            const rankVoters = votersByRank[rank];
            if (rankVoters.length === 0) return null;

            return (
              <div key={rank} className="vote-voters-rank">
                <span className="voters-rank-label">{rank}. Priorität:</span>
                <span className="voters-names">
                  {rankVoters.map((v, i) => (
                    <span key={v.user_id || i} className="voter-name">
                      {v.username || `User ${v.user_id?.substring(0, 6)}`}
                      {i < rankVoters.length - 1 && ', '}
                    </span>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="vote-option-ranking">
        <span className="ranking-label">Meine Wahl:</span>
        <div className="ranking-buttons">
          {availableRanks.map(rank => (
            <button
              key={rank}
              type="button"
              className={`rank-btn ${selectedRank === rank ? 'selected' : ''}`}
              onClick={() => onRankChange(selectedRank === rank ? null : rank)}
            >
              {rank}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default EventDetailsModal;
